import { useCallback, useEffect, useState } from "react";
import Reveal from "@/components/Reveal";

const API = process.env.REACT_APP_BACKEND_URL;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function adminFetch(url, opts = {}, retry = true) {
  return fetch(url, { credentials: "include", ...opts }).then(async (res) => {
    if (res.status === 401 && retry) {
      const r = await fetch(`${API}/api/admin/refresh`, { method: "POST", credentials: "include" });
      if (r.ok) return adminFetch(url, opts, false);
      throw Object.assign(new Error("Not authenticated"), { auth: true });
    }
    if (!res.ok) {
      let detail = null;
      try { detail = (await res.json()).detail; } catch { /* ignore */ }
      throw Object.assign(new Error(formatApiErrorDetail(detail)), { status: res.status });
    }
    return res.json();
  });
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

function fmtDay(iso) {
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

const STATUS_PILL = {
  PENDING: "border-amber-300/30 bg-amber-300/10 text-amber-300",
  APPROVED: "border-emerald-400/30 bg-emerald-400/10 text-emerald-300",
  REJECTED: "border-red-400/30 bg-red-400/10 text-red-400/80",
};

function MetricCard({ label, value, testid }) {
  return (
    <div
      className="rounded-lg border border-white/10 bg-panel p-5"
      style={{ boxShadow: "inset 0 0 24px rgba(61,107,255,0.08)" }}
      data-testid={testid}
    >
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">{label}</p>
      <p className="mt-3 font-mono text-3xl font-bold text-white">{value}</p>
    </div>
  );
}

function Chart({ series }) {
  const W = 760, H = 240, PL = 40, PR = 130, PT = 14, PB = 30;
  const max = Math.max(5, ...series.map((d) => Math.max(d.landing, d.tutorial, d.simulator_launch)));
  const x = (i) => PL + (i * (W - PL - PR)) / Math.max(1, series.length - 1);
  const y = (v) => PT + (1 - v / max) * (H - PT - PB);
  const LINES = [
    { key: "landing", label: "Landing", color: "rgba(244,244,245,0.5)" },
    { key: "tutorial", label: "Tutorial", color: "#00E5FF" },
    { key: "simulator_launch", label: "Simulator", color: "#FFB000" },
  ];
  const yTicks = [0, Math.round(max / 2), max];
  const xTickIdx = [0, Math.floor(series.length / 4), Math.floor(series.length / 2), Math.floor((3 * series.length) / 4), series.length - 1];
  return (
    <div className="overflow-x-auto" data-testid="visitors-chart">
      <svg viewBox={`0 0 ${W} ${H}`} className="h-56 w-full min-w-[560px]" role="img" aria-label="Visitors over time">
        {yTicks.map((t) => (
          <g key={t}>
            <line x1={PL} x2={W - PR} y1={y(t)} y2={y(t)} stroke="rgba(255,255,255,0.07)" strokeWidth="1" />
            <text x={PL - 8} y={y(t) + 3} textAnchor="end" fontSize="9" fill="#71717a" fontFamily="JetBrains Mono, monospace">{t}</text>
          </g>
        ))}
        {xTickIdx.map((i) => (
          <text key={i} x={x(i)} y={H - 10} textAnchor="middle" fontSize="9" fill="#71717a" fontFamily="JetBrains Mono, monospace">{series[i].date}</text>
        ))}
        {LINES.map((line) => {
          const pts = series.map((d, i) => `${x(i)},${y(d[line.key])}`).join(" ");
          const last = series[series.length - 1];
          return (
            <g key={line.key}>
              <polyline points={pts} fill="none" stroke={line.color} strokeWidth="1.5" />
              <circle cx={x(series.length - 1)} cy={y(last[line.key])} r="2.5" fill={line.color} />
              <text x={x(series.length - 1) + 8} y={y(last[line.key]) + 3} fontSize="10" fill={line.color} fontFamily="JetBrains Mono, monospace">{line.label}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function Gate({ onAuthed }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/admin/login`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (!res.ok) {
        let detail = null;
        try { detail = (await res.json()).detail; } catch { /* ignore */ }
        setError(formatApiErrorDetail(detail));
        return;
      }
      sessionStorage.setItem("qpi_admin", "1");
      onAuthed();
    } catch {
      setError("Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-6 pt-16" data-testid="admin-gate">
      <form
        onSubmit={submit}
        className="clip-frame-sm w-full max-w-sm border border-white/10 bg-panel p-8"
        style={{ boxShadow: "inset 0 0 30px rgba(61,107,255,0.1)" }}
        data-testid="admin-gate-form"
      >
        <p className="eyebrow mb-3 text-laser">— Restricted</p>
        <p className="font-mono text-xs leading-relaxed text-zinc-500">
          Enter the admin password to view the visitor dashboard.
        </p>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••••••"
          autoFocus
          data-testid="admin-password-input"
          className="mt-6 w-full rounded-lg border border-white/10 bg-black/40 px-4 py-3 font-mono text-sm text-zinc-200 placeholder-zinc-700 transition-colors focus:border-beam/60 focus:outline-none"
        />
        {error && (
          <p className="mt-3 font-mono text-xs text-laser" data-testid="admin-gate-error">{error}</p>
        )}
        <button type="submit" disabled={busy} data-testid="admin-enter-btn" className="btn-laser mt-6 w-full justify-center disabled:opacity-60">
          {busy ? "Checking…" : "Enter"}
        </button>
      </form>
    </div>
  );
}

export default function Visiting() {
  const [checking, setChecking] = useState(true);
  const [authed, setAuthed] = useState(false);
  const [stats, setStats] = useState(null);
  const [requests, setRequests] = useState(null);
  const [filter, setFilter] = useState("ALL");
  const [expandedId, setExpandedId] = useState(null);
  const [pubs, setPubs] = useState(null);
  const [pubForm, setPubForm] = useState({ title: "", authors: "", venue: "", url: "" });
  const [pubBusy, setPubBusy] = useState(false);
  const [pubError, setPubError] = useState("");
  const [pubMode, setPubMode] = useState("url");
  const [pdfFile, setPdfFile] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, r, p] = await Promise.all([
        adminFetch(`${API}/api/admin/stats`),
        adminFetch(`${API}/api/admin/requests`),
        adminFetch(`${API}/api/admin/publications`),
      ]);
      setStats(s);
      setRequests(r);
      setPubs(p);
      setAuthed(true);
    } catch (e) {
      if (e.auth) {
        sessionStorage.removeItem("qpi_admin");
        setAuthed(false);
      }
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem("qpi_admin")) {
      load();
    } else {
      setChecking(false);
    }
  }, [load]);

  const setStatus = async (id, status) => {
    try {
      await adminFetch(`${API}/api/admin/requests/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      setRequests((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
    } catch (e) {
      if (e.auth) {
        sessionStorage.removeItem("qpi_admin");
        setAuthed(false);
      }
    }
  };

  const addPublication = async (e) => {
    e.preventDefault();
    setPubError("");
    if (!pubForm.title.trim() || !pubForm.url.trim()) {
      setPubError("Title and URL are required.");
      return;
    }
    setPubBusy(true);
    try {
      await adminFetch(`${API}/api/admin/publications`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(pubForm),
      });
      setPubForm({ title: "", authors: "", venue: "", url: "" });
      setPubs(await adminFetch(`${API}/api/admin/publications`));
    } catch (err) {
      setPubError(err.message || "Could not add publication.");
    } finally {
      setPubBusy(false);
    }
  };

  const removePublication = async (id) => {
    try {
      await adminFetch(`${API}/api/admin/publications/${id}`, { method: "DELETE" });
      setPubs((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      if (err.auth) {
        sessionStorage.removeItem("qpi_admin");
        setAuthed(false);
      }
    }
  };

  const submitPdf = async (e) => {
    e.preventDefault();
    setPubError("");
    if (!pubForm.title.trim() || !pdfFile) {
      setPubError("Title and a PDF file are required.");
      return;
    }
    setPubBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", pdfFile);
      const up = await adminFetch(`${API}/api/admin/publications/upload`, {
        method: "POST",
        body: fd,
      });
      await adminFetch(`${API}/api/admin/publications`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: pubForm.title,
          authors: pubForm.authors,
          venue: pubForm.venue || "QPI Labs White Paper",
          url: `${API}/api/files/${up.path}`,
        }),
      });
      setPubForm({ title: "", authors: "", venue: "", url: "" });
      setPdfFile(null);
      setPubs(await adminFetch(`${API}/api/admin/publications`));
    } catch (err) {
      setPubError(err.message || "Could not upload the PDF.");
    } finally {
      setPubBusy(false);
    }
  };

  const logout = async () => {
    try { await fetch(`${API}/api/admin/logout`, { method: "POST", credentials: "include" }); } catch { /* ignore */ }
    sessionStorage.removeItem("qpi_admin");
    setAuthed(false);
  };

  if (checking) {
    return (
      <main className="flex min-h-screen items-center justify-center pt-16" data-testid="admin-loading">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-600">Checking session…</p>
      </main>
    );
  }

  if (!authed) {
    return (
      <main className="pt-16" data-testid="visiting-page">
        <Gate onAuthed={() => { setChecking(true); load(); }} />
      </main>
    );
  }

  const filtered = (requests || []).filter((r) => filter === "ALL" || r.status === filter);
  const noVisits = stats && stats.series.every((d) => !d.landing && !d.tutorial && !d.simulator_launch);

  return (
    <main className="min-h-screen pt-16" data-testid="visiting-page">
      <div className="container-x py-12">
        <div className="flex items-center justify-between">
          <p className="eyebrow text-laser">— Visitor Dashboard</p>
          <button onClick={logout} data-testid="admin-logout" className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 transition-colors hover:text-white">
            Log out
          </button>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <MetricCard label="Total Visits" value={stats ? stats.totalLanding : "—"} testid="metric-total-visits" />
          <MetricCard label="Unique Visitors" value={stats ? stats.uniqueVisitors : "—"} testid="metric-unique-visitors" />
          <MetricCard label="Access Requests" value={stats ? stats.accessRequests : "—"} testid="metric-access-requests" />
          <MetricCard label="Conversion Rate" value={stats ? `${stats.conversionRate}%` : "—"} testid="metric-conversion-rate" />
        </div>

        <Reveal delay={0.05}>
          <div className="clip-frame-sm mt-6 border border-white/10 bg-panel p-5 md:p-8" style={{ boxShadow: "inset 0 0 30px rgba(61,107,255,0.07)" }}>
            <p className="eyebrow mb-6">Visitors Over Time — Last 30 Days</p>
            {stats && <Chart series={stats.series} />}
            {noVisits && (
              <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-600" data-testid="no-visits-yet">
                — No visits yet
              </p>
            )}
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <div className="mt-10" data-testid="access-requests-table">
            <p className="eyebrow mb-4 text-laser">— Access Requests</p>
            <div className="mb-4 flex flex-wrap gap-2">
              {["ALL", "PENDING", "APPROVED", "REJECTED"].map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  data-testid={`requests-filter-${f.toLowerCase()}`}
                  className={`rounded-full border px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${
                    filter === f ? "border-beam/60 text-white" : "border-white/10 text-zinc-500 hover:text-zinc-300"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>

            {!requests || requests.length === 0 ? (
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-600" data-testid="no-requests-yet">
                — No requests yet
              </p>
            ) : filtered.length === 0 ? (
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-600">— None with this status</p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-white/10">
                <table className="w-full min-w-[860px] text-left font-mono text-xs">
                  <thead>
                    <tr className="border-b border-white/10 bg-black/40 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                      <th className="px-4 py-3 font-medium">Date</th>
                      <th className="px-4 py-3 font-medium">Email</th>
                      <th className="px-4 py-3 font-medium">Affiliation</th>
                      <th className="px-4 py-3 font-medium">Message</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3 font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((r) => (
                      <tr key={r.id} className="border-b border-white/5 text-zinc-300 last:border-0" data-testid={`request-row-${r.id}`}>
                        <td className="whitespace-nowrap px-4 py-3 text-zinc-500">{fmtDate(r.timestamp)}</td>
                        <td className="px-4 py-3">
                          <a href={`mailto:${r.email}`} data-testid={`request-email-${r.id}`} className="text-beam hover:underline">{r.email}</a>
                        </td>
                        <td className="max-w-[140px] truncate px-4 py-3 text-zinc-400" title={r.affiliation}>{r.affiliation || "—"}</td>
                        <td
                          className="max-w-[280px] cursor-pointer px-4 py-3 text-zinc-400"
                          title={r.message}
                          onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}
                          data-testid={`request-message-${r.id}`}
                        >
                          {r.message
                            ? expandedId === r.id || r.message.length <= 60
                              ? r.message
                              : `${r.message.slice(0, 60)}…`
                            : "—"}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] ${STATUS_PILL[r.status] || STATUS_PILL.PENDING}`} data-testid={`request-status-${r.id}`}>
                            {r.status}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <button
                            onClick={() => setStatus(r.id, "APPROVED")}
                            data-testid={`request-approve-${r.id}`}
                            className="rounded border border-white/15 px-2 py-1 text-[10px] uppercase tracking-[0.1em] text-zinc-400 transition-colors hover:border-emerald-400/60 hover:text-emerald-300"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => setStatus(r.id, "REJECTED")}
                            data-testid={`request-reject-${r.id}`}
                            className="ml-2 rounded border border-white/15 px-2 py-1 text-[10px] uppercase tracking-[0.1em] text-zinc-400 transition-colors hover:border-red-400/60 hover:text-red-400"
                          >
                            Reject
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <div className="mt-10" data-testid="publications-manager">
            <p className="eyebrow mb-4 text-laser">— Publications</p>
            <p className="mb-5 font-mono text-xs leading-relaxed text-zinc-500">
              Add papers, white papers, or patents — they appear on the public Publications page
              instantly.
            </p>
            <div className="mb-4 flex gap-2" data-testid="publication-mode-toggle">
              {[["url", "Add paper URL"], ["pdf", "Upload PDF"]].map(([mode, label]) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => { setPubMode(mode); setPubError(""); }}
                  data-testid={`publication-mode-${mode}`}
                  className={`rounded-full border px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${
                    pubMode === mode ? "border-beam/60 text-white" : "border-white/10 text-zinc-500 hover:text-zinc-300"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {pubMode === "url" ? (
            <form onSubmit={addPublication} className="grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="publication-form">
              <div className="md:col-span-2">
                <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Title *</label>
                <input
                  value={pubForm.title}
                  onChange={(e) => setPubForm({ ...pubForm, title: e.target.value })}
                  placeholder="Paper title"
                  data-testid="publication-title-input"
                  className="w-full rounded-lg border border-white/10 bg-black/40 px-4 py-3 font-mono text-sm text-zinc-200 placeholder-zinc-600 transition-colors focus:border-beam/60 focus:outline-none"
                />
              </div>
              <div>
                <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Authors</label>
                <input
                  value={pubForm.authors}
                  onChange={(e) => setPubForm({ ...pubForm, authors: e.target.value })}
                  placeholder="Falkenberg et al."
                  data-testid="publication-authors-input"
                  className="w-full rounded-lg border border-white/10 bg-black/40 px-4 py-3 font-mono text-sm text-zinc-200 placeholder-zinc-600 transition-colors focus:border-beam/60 focus:outline-none"
                />
              </div>
              <div>
                <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Venue / Year</label>
                <input
                  value={pubForm.venue}
                  onChange={(e) => setPubForm({ ...pubForm, venue: e.target.value })}
                  placeholder="IEEE Trans. · arXiv · Patent · 2026"
                  data-testid="publication-venue-input"
                  className="w-full rounded-lg border border-white/10 bg-black/40 px-4 py-3 font-mono text-sm text-zinc-200 placeholder-zinc-600 transition-colors focus:border-beam/60 focus:outline-none"
                />
              </div>
              <div className="md:col-span-2">
                <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">URL (arXiv or PDF) *</label>
                <input
                  value={pubForm.url}
                  onChange={(e) => setPubForm({ ...pubForm, url: e.target.value })}
                  placeholder="https:// — arXiv, IEEE, DOI, or patent link"
                  data-testid="publication-url-input"
                  className="w-full rounded-lg border border-white/10 bg-black/40 px-4 py-3 font-mono text-sm text-zinc-200 placeholder-zinc-600 transition-colors focus:border-beam/60 focus:outline-none"
                />
              </div>
              <div className="md:col-span-2">
                <button
                  type="submit"
                  disabled={pubBusy}
                  data-testid="publication-add-btn"
                  className="w-full rounded-full border border-beam/50 bg-black/40 px-6 py-3 font-mono text-xs font-bold uppercase tracking-widest text-white transition-all duration-300 hover:border-beam hover:shadow-[0_0_24px_rgba(0,229,255,0.35)] disabled:opacity-60"
                >
                  {pubBusy ? "Adding…" : "Add Publication"}
                </button>
                {pubError && (
                  <p className="mt-3 font-mono text-xs text-laser" data-testid="publication-error">{pubError}</p>
                )}
              </div>
            </form>
            ) : (
            <form onSubmit={submitPdf} className="grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="publication-pdf-form">
              <div className="md:col-span-2">
                <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Title *</label>
                <input
                  value={pubForm.title}
                  onChange={(e) => setPubForm({ ...pubForm, title: e.target.value })}
                  placeholder="Paper title"
                  data-testid="publication-pdf-title-input"
                  className="w-full rounded-lg border border-white/10 bg-black/40 px-4 py-3 font-mono text-sm text-zinc-200 placeholder-zinc-600 transition-colors focus:border-beam/60 focus:outline-none"
                />
              </div>
              <div>
                <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Authors</label>
                <input
                  value={pubForm.authors}
                  onChange={(e) => setPubForm({ ...pubForm, authors: e.target.value })}
                  placeholder="Falkenberg et al."
                  data-testid="publication-pdf-authors-input"
                  className="w-full rounded-lg border border-white/10 bg-black/40 px-4 py-3 font-mono text-sm text-zinc-200 placeholder-zinc-600 transition-colors focus:border-beam/60 focus:outline-none"
                />
              </div>
              <div>
                <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Venue / Year</label>
                <input
                  value={pubForm.venue}
                  onChange={(e) => setPubForm({ ...pubForm, venue: e.target.value })}
                  placeholder="QPI Labs White Paper · 2026"
                  data-testid="publication-pdf-venue-input"
                  className="w-full rounded-lg border border-white/10 bg-black/40 px-4 py-3 font-mono text-sm text-zinc-200 placeholder-zinc-600 transition-colors focus:border-beam/60 focus:outline-none"
                />
              </div>
              <div className="md:col-span-2">
                <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">PDF file * (max 20 MB)</label>
                <label className="inline-flex cursor-pointer items-center gap-3 rounded-lg border border-white/10 bg-black/40 px-4 py-3 font-mono text-sm text-zinc-300 transition-colors hover:border-beam/60">
                  {pdfFile ? pdfFile.name : "Choose PDF"}
                  <input
                    type="file"
                    accept="application/pdf,.pdf"
                    className="hidden"
                    onChange={(e) => setPdfFile(e.target.files[0] || null)}
                    data-testid="publication-pdf-input"
                  />
                </label>
              </div>
              <div className="md:col-span-2">
                <button
                  type="submit"
                  disabled={pubBusy}
                  data-testid="publication-upload-btn"
                  className="w-full rounded-full border border-beam/50 bg-black/40 px-6 py-3 font-mono text-xs font-bold uppercase tracking-widest text-white transition-all duration-300 hover:border-beam hover:shadow-[0_0_24px_rgba(0,229,255,0.35)] disabled:opacity-60"
                >
                  {pubBusy ? "Uploading…" : "Upload & Publish"}
                </button>
                {pubError && (
                  <p className="mt-3 font-mono text-xs text-laser" data-testid="publication-error">{pubError}</p>
                )}
              </div>
            </form>
            )}

            {!pubs || pubs.length === 0 ? (
              <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-600" data-testid="no-publications-yet">
                — No publications yet
              </p>
            ) : (
              <div className="mt-6 overflow-x-auto rounded-lg border border-white/10">
                <table className="w-full min-w-[560px] text-left font-mono text-xs">
                  <thead>
                    <tr className="border-b border-white/10 bg-black/40 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                      <th className="px-4 py-3 font-medium">Title</th>
                      <th className="px-4 py-3 font-medium">Authors</th>
                      <th className="px-4 py-3 font-medium">Venue</th>
                      <th className="px-4 py-3 font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pubs.map((p) => (
                      <tr key={p.id} className="border-b border-white/5 text-zinc-300 last:border-0">
                        <td className="max-w-[280px] truncate px-4 py-3">{p.title}</td>
                        <td className="px-4 py-3 text-zinc-500">{p.authors || "—"}</td>
                        <td className="px-4 py-3 text-zinc-500">{p.venue || "—"}</td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => removePublication(p.id)}
                            data-testid={`publication-remove-${p.id}`}
                            className="rounded border border-white/15 px-2 py-1 text-[10px] uppercase tracking-[0.1em] text-zinc-400 transition-colors hover:border-red-400/60 hover:text-red-400"
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <div className="mt-10" data-testid="return-visitors-table">
            <p className="eyebrow mb-4 text-laser">— Return Visitors</p>
            {!stats || stats.returnVisitors.length === 0 ? (
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-600" data-testid="no-return-visitors-yet">
                — No return visitors yet
              </p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-white/10">
                <table className="w-full min-w-[640px] text-left font-mono text-xs">
                  <thead>
                    <tr className="border-b border-white/10 bg-black/40 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                      <th className="px-4 py-3 font-medium">Session</th>
                      <th className="px-4 py-3 font-medium">Visits</th>
                      <th className="px-4 py-3 font-medium">First Seen</th>
                      <th className="px-4 py-3 font-medium">Last Seen</th>
                      <th className="px-4 py-3 font-medium">Pages Visited</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.returnVisitors.map((v) => (
                      <tr key={v.session_id} className="border-b border-white/5 text-zinc-300 last:border-0">
                        <td className="px-4 py-3 text-zinc-600">{v.session_id.slice(0, 8)}…</td>
                        <td className="px-4 py-3 text-beam">{v.count}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-zinc-500">{fmtDay(v.first)}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-zinc-500">{fmtDay(v.last)}</td>
                        <td className="px-4 py-3 text-zinc-400">{v.pages.join(", ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Reveal>
      </div>
    </main>
  );
}
